import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Stack,
    Typography,
    IconButton,
    Alert,
    Chip,
    Tabs,
    Tab,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import VisibilityIcon from "@mui/icons-material/Visibility";

function toImgUrl(pathOrUrl, backendBase) {
    if (!pathOrUrl) return "";
    if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
    return `${backendBase}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function PreviewPanel({ coverPreview, logoUrl, logoPosition }) {
    const { t } = useTranslation();
    return (
        <>
            {/* Outer wrapper — relative, no overflow:hidden so logo protrudes freely */}
            <Box sx={{ position: "relative", width: "100%" }}>

                {/* Cover — overflow:hidden stays here only */}
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: 3,
                        overflow: "hidden",
                        position: "relative",
                        "&::before": { content: '""', display: "block", paddingTop: "33%" },
                        bgcolor: "rgba(0,0,0,0.25)",
                        border: "1px solid",
                        borderColor: "rgba(255,255,255,0.25)",
                    }}
                >
                    {coverPreview ? (
                        <Box
                            component="img"
                            src={coverPreview}
                            alt="Εξώφυλλο"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "grid",
                                placeItems: "center",
                                bgcolor: "rgba(0,0,0,0.35)",
                            }}
                        >
                            <Typography variant="body2" sx={{ color: "#fff" }}>
                                {t("modals.cover.noCover")}
                            </Typography>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PhotoCameraIcon />}
                        disabled
                        sx={{ position: "absolute", top: 12, right: 12, pointerEvents: "none" }}
                    >
                        {t("modals.cover.uploadCover")}
                    </Button>
                </Box>

                {/* Logo — sibling of cover, half-in/half-out, matches louder-front exactly */}
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        bottom: "12%",
                        transform: "translateX(-50%) translateY(50%)",
                        width: "12%",
                        aspectRatio: "1",
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor: "common.white",
                        bgcolor: "common.white",
                        overflow: "hidden",
                        boxShadow: "0 6px 28px rgba(0,0,0,0.18)",
                    }}
                >
                    {logoUrl ? (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt="Λογότυπο"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: `${logoPosition?.x ?? 50}% ${logoPosition?.y ?? 50}%`,
                                transform: `scale(${logoPosition?.scale ?? 1})`,
                                transformOrigin: `${logoPosition?.x ?? 50}% ${logoPosition?.y ?? 50}%`,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0,0,0,0.12)",
                            }}
                        >
                            <PhotoCameraIcon sx={{ fontSize: "40%", color: "rgba(0,0,0,0.35)" }} />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Spacer — clears the protruding logo half, matches louder-front SPACER_PT */}
            <Box sx={{ paddingTop: "10%" }} />
        </>
    );
}


export default function CoverUploadModal({
    open,
    onClose,
    onSaved,
    currentCoverUrl = "",
    logoUrl = "",
    logoPosition = {},
    backendBase = "",
    token = "",
    notify,
}) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [tab, setTab] = useState(0);
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    useEffect(() => {
        if (open) {
            setFile(null);
            setPreviewUrl("");
            setErr("");
            setTab(0);
        }
    }, [open]);

    const handleFileChange = useCallback(
        (e) => {
            const picked = e.target.files?.[0];
            e.target.value = "";

            if (!picked) return;

            if (!picked.type.startsWith("image/")) {
                const msg = t("modals.common.notImageFile");
                setErr(msg);
                notify?.("error", msg);
                return;
            }

            setErr("");

            if (previewUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(previewUrl);
            }

            setFile(picked);
            setPreviewUrl(URL.createObjectURL(picked));
        },
        [previewUrl, notify]
    );

    const handleSave = async () => {
        if (!file) {
            const msg = t("modals.common.noFileSelected");
            setErr(msg);
            notify?.("error", msg);
            return;
        }

        setErr("");
        setSaving(true);

        try {
            const formData = new FormData();
            formData.append("coverImg", file);

            const res = await axios.post(
                `${backendBase}/api/admin/images/client/cover`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const data = res.data || {};
            const newCoverPath = data?.data?.coverImg || data?.coverImg || "";
            const message = data?.message || t("modals.cover.uploadSuccess");

            onSaved?.(newCoverPath);
            notify?.("success", message);
            onClose();
        } catch (e) {
            console.error("Cover upload failed:", e);

            const message =
                e?.response?.data?.message ||
                e?.message ||
                t("modals.cover.uploadFailed");

            setErr(message);
            notify?.("error", message);
        } finally {
            setSaving(false);
        }
    };

    const resolvedCover = previewUrl || (currentCoverUrl ? toImgUrl(currentCoverUrl, backendBase) : "");

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: "#1a1a2e",
                    color: "#fff",
                    borderRadius: 3,
                    border: "1px solid rgba(255,255,255,0.12)",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <PhotoCameraIcon sx={{ color: "#fff" }} />
                    <Typography fontWeight={900} variant="h6">
                        {t("modals.cover.title")}
                    </Typography>
                </Stack>

                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: "rgba(255,255,255,0.6)" }}
                    disabled={saving}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                    px: 2,
                    pt: 1,
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    "& .MuiTab-root": {
                        color: "rgba(255,255,255,0.6)",
                        minHeight: 44,
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        textTransform: "none",
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                    },
                    "& .MuiTab-root.Mui-selected": {
                        color: "#111 !important",
                        backgroundColor: "#fff",
                    },
                    "& .MuiTab-root.Mui-selected .MuiSvgIcon-root": {
                        color: "#111 !important",
                    },
                    "& .MuiTab-root:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                    },
                    "& .MuiTabs-indicator": {
                        display: "none",
                    },
                }}
            >
                <Tab
                    icon={<PhotoCameraIcon fontSize="small" />}
                    iconPosition="start"
                    label={t("common.select")}
                />
                <Tab
                    icon={<VisibilityIcon fontSize="small" />}
                    iconPosition="start"
                    label={t("common.preview")}
                    disabled={!resolvedCover}
                />
            </Tabs>

            <DialogContent sx={{ pt: 2.5 }}>
                {tab === 0 && (
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}>
                                {t("modals.cover.fileInstruction")}
                            </Typography>

                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff" }}
                                >
                                    {t("modals.common.selectFile")}
                                </Button>

                                {file && (
                                    <Chip
                                        label={file.name}
                                        size="small"
                                        onDelete={() => {
                                            setFile(null);
                                            if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
                                            setPreviewUrl("");
                                        }}
                                        sx={{
                                            bgcolor: "rgba(255,255,255,0.1)",
                                            color: "#fff",
                                            "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.6)" },
                                        }}
                                    />
                                )}
                            </Stack>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleFileChange}
                            />
                        </Box>

                        {resolvedCover && (
                            <Box>
                                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 1.5 }}>
                                    {t("modals.cover.roughPreview")}
                                </Typography>
                                <PreviewPanel
                                    coverPreview={resolvedCover}
                                    logoUrl={logoUrl}
                                    logoPosition={logoPosition}
                                />
                            </Box>
                        )}

                        {err && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {err}
                            </Alert>
                        )}
                    </Stack>
                )}

                {tab === 1 && (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{ color: "rgba(255,255,255,0.6)", mb: 2, textAlign: "center" }}
                        >
                            {t("modals.cover.previewNote")}
                        </Typography>
                        <PreviewPanel
                            coverPreview={resolvedCover}
                            logoUrl={logoUrl}
                            logoPosition={logoPosition}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 2.5,
                    pt: 1.5,
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    gap: 1,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={saving}
                    sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
                >
                    {t("common.cancel")}
                </Button>

                {tab === 0 && resolvedCover && (
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setTab(1)}
                        disabled={saving}
                        sx={{ borderColor: "#fff", color: "#fff", flex: 1 }}
                    >
                        {t("common.preview")}
                    </Button>
                )}

                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!file || saving}
                    sx={{ flex: 1 }}
                >
                    {saving ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} color="inherit" />
                            <span>{t("common.saving")}</span>
                        </Stack>
                    ) : (
                        t("common.save")
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}